import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Auswahl-Modus des mobilen Editors — reiner UI-State, keine Mutationen.
 *
 * `start(id)` schaltet den Modus ein (Long-Press auf eine Kachel), `toggle(id)` markiert
 * und demarkiert im laufenden Modus, `clear()` beendet ihn, `restore(idList)` setzt die
 * Auswahl auf eine gegebene Liste (Wiederholung nach Teil-Fehlschlag einer Sammel-Aktion).
 * Die Markierungen hängen an Karten-IDs, nicht an der Sichtbarkeit: ein Such- oder
 * Filterwechsel lässt sie stehen. Das Demarkieren der letzten Karte beendet den Modus — die
 * natürliche Rücknahme des Einstiegs (Long-Press auf die erste Karte); dasselbe gilt für
 * `restore([])`. Ein Deckwechsel (`deckId`) beendet den Modus ebenfalls, weil die IDs dann
 * zu einem anderen Deck gehören.
 */
export function useCardSelection(deckId) {
  const [state, setState] = useState(() => ({ active: false, ids: new Set() }));

  useEffect(() => {
    setState({ active: false, ids: new Set() });
  }, [deckId]);

  const start = useCallback((id) => {
    if (id == null) return;
    setState({ active: true, ids: new Set([id]) });
  }, []);

  const toggle = useCallback((id) => {
    if (id == null) return;
    setState((prev) => {
      const ids = new Set(prev.ids);
      if (ids.has(id)) ids.delete(id);
      else ids.add(id);
      // Ohne Markierung gibt es keinen Modus mehr — das Abwählen der letzten Karte ist die
      // natürliche Rücknahme des Einstiegs (langer Druck auf die erste Karte).
      return { active: ids.size > 0, ids };
    });
  }, []);

  const clear = useCallback(() => {
    setState({ active: false, ids: new Set() });
  }, []);

  /** Stellt eine Auswahl wieder her (Wiederholung nach Teil-Fehlschlag). Leer = Modus aus. */
  const restore = useCallback((idList) => {
    const ids = new Set(idList || []);
    setState({ active: ids.size > 0, ids });
  }, []);

  return useMemo(
    () => ({
      active: state.active, ids: state.ids, count: state.ids.size, start, toggle, clear, restore,
    }),
    [state, start, toggle, clear, restore]
  );
}

export default useCardSelection;
